import React, { useState, useEffect } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import moment from 'moment'
import PropTypes from 'prop-types'
import { COLORS } from '@common'
import { logger } from '@utils'
import './OrderDetailModal.css'

const OrderDetailModal = ({ isOpen, toggle, order, getOrderStatus, getOrderStatusClass, getOrderType }) => {
    // eslint-disable-next-line
    const [isCopied, setIsCopied] = useState(false)

    // Reset copy state when modal opens
    useEffect(() => {
        if (isOpen) {
            setIsCopied(false)
        }
    }, [isOpen])

    if (!order) return null

    // Handle copy order ID to clipboard
    // eslint-disable-next-line
    const handleCopyOrderId = async () => {
        try {
            const orderId = order.orderId || ''
            await navigator.clipboard.writeText(orderId)
            setIsCopied(true)
            setTimeout(() => {
                setIsCopied(false)
            }, 2000)
        } catch (err) {
            logger.error('Failed to copy order ID:', err)
        }
    }

    // const formatDate = dateString => {
    //     if (!dateString) return ''
    //     return moment(dateString).format('MMM DD, YYYY')
    // }

    const formatDateTime = dateString => {
        if (!dateString) return ''
        return moment(dateString).format('MMM DD, YYYY @ HH:mm')
    }

    // Calculate dish total with selections
    const calculateDishTotal = dish => {
        let total = 0
        if (dish.variants && dish.variants.length > 0) {
            dish.variants.forEach(variant => {
                const variantTotal = variant.sellPrice * variant.quantity
                total += variantTotal

                // Add selections cost
                if (dish.selections && dish.selections.length > 0) {
                    dish.selections.forEach(selection => {
                        if (selection.options && selection.options.length > 0) {
                            selection.options.forEach(option => {
                                total += option.sellPrice * option.quantity * variant.quantity
                            })
                        }
                    })
                }
            })
        }
        return total
    }

    // Group variants by variantId
    const groupVariantsByVariantId = variants => {
        const variantGroups = {}
        variants?.forEach(variant => {
            const key = variant.variantId || variant.id || 'no-variant-id'
            if (!variantGroups[key]) {
                variantGroups[key] = {
                    variantId: variant.variantId,
                    variants: [],
                    totalQuantity: 0,
                    totalPrice: 0,
                }
            }
            variantGroups[key].variants.push(variant)
            variantGroups[key].totalQuantity += variant.quantity || 0
            variantGroups[key].totalPrice += (variant.sellPrice || 0) * (variant.quantity || 0)
        })
        return Object.values(variantGroups)
    }

    // Render dish item with grouped variants
    const renderDishItem = (dish, dishIndex) => {
        const variantGroupArray = groupVariantsByVariantId(dish.variants)

        return (
            <div key={dish.id || dishIndex} className="dish-item">
                {variantGroupArray.map((group, groupIndex) => {
                    const firstVariant = group.variants[0]
                    const isDefaultTitle = firstVariant.name === 'Default Title'
                    // Show dish name for first group, or if there are multiple groups (different variantIds)
                    const shouldShowDishName = groupIndex === 0 || variantGroupArray.length > 1

                    return (
                        <div key={group.variantId || groupIndex} className="variant-item">
                            {
                                isDefaultTitle ?
                                    // Combined layout for Default Title
                                    <div className="variant-header-combined">
                                        {shouldShowDishName && (
                                            <div className="dish-name-inline">
                                                <h5 className="dish-name">{dish.name}</h5>
                                                {dish.note && <p className="dish-note">{dish.note}</p>}
                                            </div>
                                        )}
                                        <span className="variant-quantity">x{group.totalQuantity}</span>
                                        <span className="variant-price">${group.totalPrice.toFixed(2)}</span>
                                    </div>
                                    // Separate layout for non-Default Title
                                :   <>
                                        {shouldShowDishName && (
                                            <div className="dish-header">
                                                <h5 className="dish-name">{dish.name}</h5>
                                                {dish.note && <p className="dish-note">{dish.note}</p>}
                                            </div>
                                        )}
                                        <div className="variant-header">
                                            <span className="variant-name">{firstVariant.name}</span>
                                            <span className="variant-quantity">x{group.totalQuantity}</span>
                                            <span className="variant-price">${group.totalPrice.toFixed(2)}</span>
                                        </div>
                                    </>

                            }

                            {/* Selections - use this group's total quantity */}
                            {dish.selections?.map((selection, selectionIndex) => (
                                <div key={selection.id || selectionIndex} className="selection-group">
                                    {selection.options?.map((option, optionIndex) => (
                                        <div key={option.id || optionIndex} className="selection-item">
                                            <span className="selection-name">+ {option.name}</span>
                                            {option.sellPrice > 0 && (
                                                <span className="selection-quantity">
                                                    x{option.quantity * group.totalQuantity}
                                                </span>
                                            )}
                                            <span className="selection-price">
                                                ${(option.sellPrice * option.quantity * group.totalQuantity).toFixed(2)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )
                })}

                <div className="dish-total">
                    <span className="dish-total-label">Dish Subtotal:</span>
                    <span className="dish-total-value">${calculateDishTotal(dish).toFixed(2)}</span>
                </div>
            </div>
        )
    }

    // Group dishes by vendor
    const groupedDishes =
        order.dishes?.reduce((acc, dish) => {
            const vendorName = dish.vendorName || 'Unknown Vendor'
            if (!acc[vendorName]) {
                acc[vendorName] = []
            }
            acc[vendorName].push(dish)
            return acc
        }, {}) || {}

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered fullscreen="lg" className="order-detail-modal" style={{ margin: 0 }}>
            <ModalHeader toggle={toggle} className="order-detail-header">
                <div className="order-detail-title">Order Details</div>
                <div className="order-detail-number-container">
                    <p className="order-detail-number">ORD-{order.number || order.orderId}</p>
                    {/* <button
                        className="copy-order-id-btn"
                        onClick={handleCopyOrderId}
                        title={isCopied ? 'Copied!' : 'Copy Order ID'}
                    >
                        <i className={isCopied ? 'bx bx-check' : 'bx bx-copy'}></i>
                        {isCopied && <span className="copy-feedback">Copied!</span>}
                    </button> */}
                </div>
            </ModalHeader>
            <ModalBody className="order-detail-body">
                {/* Order Info Section */}
                <div className="order-info-section">
                    <div className="info-row">
                        <span className="info-label">Status:</span>
                        <span className={`order-status-badge ${getOrderStatusClass(order)}`}>{getOrderStatus(order)}</span>
                    </div>
                    <div className="info-row">
                        <span className="info-label">Created At:</span>
                        <span className="info-value">{formatDateTime(order.createdOn)}</span>
                    </div>
                    {order.pickupScheduledAt && (
                        <div className="info-row">
                            <span className="info-label">Pickup At:</span>
                            <span className="info-value">{formatDateTime(order.pickupScheduledAt)}</span>
                        </div>
                    )}
                    <div className="info-row">
                        <span className="info-label">Order Type:</span>
                        <span className="info-value">{getOrderType(order)}</span>
                    </div>
                    {order.note && (
                        <div className="info-row">
                            <span className="info-label">Note:</span>
                            <span className="info-value">{order.note}</span>
                        </div>
                    )}
                </div>

                {/* Dishes Section */}
                <div className="dishes-section">
                    {Object.entries(groupedDishes).map(([vendorName, dishes]) => (
                        <div key={vendorName} className="vendor-group">
                            <h4 className="vendor-name">{vendorName}</h4>
                            {dishes.map((dish, dishIndex) => renderDishItem(dish, dishIndex))}
                        </div>
                    ))}
                </div>

                {/* Payment Info (if exists) */}
                {order.payment && (
                    <div className="payment-info">
                        <div className="info-row">
                            <span className="info-label">Payment Status:</span>
                            <span className="info-value">{order.payment.type || 'Paid'}</span>
                        </div>
                        {order.payment.paidAt && (
                            <div className="info-row">
                                <span className="info-label">Paid At:</span>
                                <span className="info-value">{formatDateTime(order.payment.paidAt)}</span>
                            </div>
                        )}
                        {order.status === 'CANCELLED' && (
                            <div
                                style={{
                                    marginTop: '16px',
                                    padding: '12px 16px',
                                    backgroundColor: COLORS.INFO.background,
                                    borderRadius: '8px',
                                    border: `1px solid ${COLORS.INFO.border}`,
                                    fontSize: '14px',
                                    lineHeight: '1.6',
                                    color: COLORS.INFO.text,
                                }}
                            >
                                This order has entered the refund process. The refund will be credited to your account within{' '}
                                <strong>5-7 business days</strong>. If you have any questions, please contact the Cashier.
                            </div>
                        )}
                    </div>
                )}
            </ModalBody>
            <ModalFooter className="order-detail-footer">
                <div className="total-section">
                    {order.summary?.originalAmount !== undefined && (
                        <div className="total-row discount-row">
                            <span className="total-label">Subtotal:</span>
                            <span className="total-value">${(order.summary.originalAmount || 0).toFixed(2)}</span>
                        </div>
                    )}
                    {order.summary?.benefitDiscount > 0 && (
                        <div className="total-row discount-row">
                            <span className="total-label">Staff Benefit Discount:</span>
                            <span className="total-value discount-value">
                                -${(order.summary.benefitDiscount || 0).toFixed(2)}
                            </span>
                        </div>
                    )}
                    {order.summary?.creditDiscount > 0 && (
                        <div className="total-row discount-row">
                            <span className="total-label">Points Discount:</span>
                            <span className="total-value discount-value">-${(order.summary.creditDiscount || 0).toFixed(2)}</span>
                        </div>
                    )}
                    {order.summary?.promoDiscount > 0 && (
                        <div className="total-row discount-row">
                            <span className="total-label">Promo Discount:</span>
                            <span className="total-value discount-value">-${(order.summary.promoDiscount || 0).toFixed(2)}</span>
                        </div>
                    )}
                    <div className="total-row">
                        <span className="total-label">Total Amount:</span>
                        <span className="total-value">
                            {order.summary?.finalAmount === 0 ? 'Free' : `$${(order.summary?.finalAmount || 0).toFixed(2)}`}
                        </span>
                    </div>
                </div>
            </ModalFooter>
        </Modal>
    )
}

OrderDetailModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    order: PropTypes.object,
    getOrderStatus: PropTypes.func.isRequired,
    getOrderStatusClass: PropTypes.func.isRequired,
    getOrderType: PropTypes.func.isRequired,
}

export default OrderDetailModal
